import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Trash2, Database, RefreshCw, ShieldCheck, CreditCard } from "lucide-react";
import { apiErrorMessage, endpoints, type CertificateItem } from "@/api/client";
import { bootstrapOperatoreContext } from "@/lib/bootstrapOperatore";
import { useOperatoreContext } from "@/lib/operatoreContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { CenteredLoader, ErrorState } from "@/components/ui/feedback";
import { P12SetupWizard } from "@/components/P12SetupWizard";
import { PreferenzeOperative } from "@/components/PreferenzeOperative";
import { formatDate } from "@/lib/utils";

export function Impostazioni() {
  const qc = useQueryClient();
  const ctx = useOperatoreContext();
  const syncRuns = useQuery({ queryKey: ["sync-runs"], queryFn: endpoints.sync.runs });
  const certs = useQuery({ queryKey: ["certs"], queryFn: endpoints.certificates.list });
  const hasCert = (certs.data?.length ?? 0) > 0;
  const rentriHealth = useQuery({
    queryKey: ["health-rentri"],
    queryFn: endpoints.rentriHealth,
    retry: false,
    enabled: hasCert && !certs.isLoading,
  });
  const [bootstrapping, setBootstrapping] = useState(false);

  const syncReady = !!ctx.numIscr && !!ctx.numIscrSito;
  const canSync = hasCert && syncReady;

  useEffect(() => {
    if (!hasCert || syncReady) return;
    let cancelled = false;
    setBootstrapping(true);
    void bootstrapOperatoreContext(ctx)
      .then((r) => {
        if (cancelled || r.ok) return;
        if (r.missing === "cf") {
          toast.info("Inserisci CF/P.IVA nella barra laterale per collegare l'operatore RENTRI.");
        }
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setBootstrapping(false);
      });
    return () => {
      cancelled = true;
    };
  }, [hasCert, syncReady, ctx.cf, ctx.numIscr, ctx.numIscrSito]);

  const billingQ = useQuery({
    queryKey: ["billing-status"],
    queryFn: endpoints.billing.status,
  });
  const portalMut = useMutation({
    mutationFn: () =>
      endpoints.billing.portal(`${window.location.origin}/impostazioni`),
    onSuccess: (d) => {
      window.location.href = d.url;
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Errore portal"),
  });

  const syncPull = useMutation({
    mutationFn: () => {
      if (!hasCert) {
        throw new Error("Carica il certificato P12 nel wizard interoperabilità prima di sincronizzare.");
      }
      return endpoints.sync.pullAll({
        num_iscr: ctx.numIscr,
        num_iscr_sito: ctx.numIscrSito,
        registro_id: ctx.registroId || undefined,
      });
    },
    onSuccess: (data) => {
      const partial = data && typeof data === "object" && "partial" in data && (data as { partial?: boolean }).partial;
      const errs = partial && "errors" in data ? (data as { errors?: string[] }).errors : undefined;
      if (partial && errs?.length) {
        toast.warning(`Sincronizzazione parziale (${errs.join(", ")} non aggiornati)`);
      } else {
        toast.success("Sincronizzazione completata");
      }
      qc.invalidateQueries({ queryKey: ["sync-runs"] });
      qc.invalidateQueries({ queryKey: ["registri"] });
      qc.invalidateQueries({ queryKey: ["movimenti"] });
      qc.invalidateQueries({ queryKey: ["formulari"] });
      qc.invalidateQueries({ queryKey: ["fir-trasmissioni"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  const remove = useMutation({
    mutationFn: (id: string) => endpoints.certificates.remove(id),
    onSuccess: () => {
      toast.success("Certificato eliminato");
      qc.invalidateQueries({ queryKey: ["certs"] });
      qc.invalidateQueries({ queryKey: ["certs-home"] });
      qc.removeQueries({ queryKey: ["health-rentri"] });
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Impostazioni</h1>
        <p className="text-sm text-muted-foreground">
          Interoperabilità RENTRI, certificati e preferenze operative
        </p>
      </div>

      <P12SetupWizard />

      {billingQ.data && !billingQ.data.billing_disabled && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <CreditCard className="h-4 w-4" /> Abbonamento e utilizzo
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex flex-wrap gap-2">
              <Badge variant={billingQ.data.subscription_active ? "default" : "secondary"}>
                {billingQ.data.subscription_active
                  ? `Piano attivo${billingQ.data.subscription_status ? ` (${billingQ.data.subscription_status})` : ""}`
                  : "Piano non attivo"}
              </Badge>
              <Badge variant="outline">
                CER {billingQ.data.cer_used}/{billingQ.data.cer_slots}
              </Badge>
              <Badge variant="outline">
                Movimenti ~{billingQ.data.movimenti_used}/{billingQ.data.movimenti_limit}
              </Badge>
              <Badge variant="outline">
                xFIR ~{billingQ.data.xfir_used}/{billingQ.data.xfir_limit}
              </Badge>
            </div>
            <p className="text-muted-foreground text-xs">
              I limiti movimenti/xFIR sono indicativi e non bloccano l&apos;uso dell&apos;app.
            </p>
            <div className="flex flex-wrap gap-2">
              {!billingQ.data.subscription_active && (
                <Button size="sm" asChild>
                  <a href="/abbonamento">Attiva abbonamento</a>
                </Button>
              )}
              {billingQ.data.subscription_active && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => portalMut.mutate()}
                  disabled={portalMut.isPending}
                >
                  Gestione fatturazione Stripe
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <PreferenzeOperative />

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <RefreshCw className="h-4 w-4" /> Sincronizza con RENTRI
          </CardTitle>
          <Button
            size="sm"
            disabled={!canSync || syncPull.isPending || bootstrapping}
            onClick={() => {
              if (!hasCert) {
                toast.error("Carica il certificato P12 prima di sincronizzare con RENTRI.");
                return;
              }
              if (!syncReady) {
                toast.info("Completa la configurazione operatore (CF, sito) nella barra laterale.");
                return;
              }
              syncPull.mutate();
            }}
          >
            {syncPull.isPending
              ? "Sincronizzazione…"
              : bootstrapping
                ? "Configurazione operatore…"
                : "Sincronizza ora"}
          </Button>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-muted-foreground">
          <p>
            Scarica registri, movimenti (se registro selezionato) e FIR dal portale governo nel database
            del tuo account.
          </p>
          {!syncReady && hasCert && (
            <p className="text-amber-700">
              {bootstrapping
                ? "Recupero operatore e unità locale da RENTRI…"
                : !ctx.cf || ctx.cf.length < 11
                  ? "Inserisci CF/P.IVA nella barra laterale (o completa il wizard operatore) per abilitare la sincronizzazione."
                  : !ctx.numIscr
                    ? "Operatore RENTRI non trovato per questo CF: verifica l'iscrizione su RENTRI."
                    : "Nessuna unità locale disponibile per questo operatore su RENTRI."}
            </p>
          )}
          {!hasCert && (
            <p className="text-amber-700">Carica prima il certificato P12 nel wizard interoperabilità.</p>
          )}
          {syncReady && ctx.numIscrSito && (
            <p className="text-xs">
              Operatore: <span className="font-mono">{ctx.numIscr}</span>
              {" · "}
              Sito: <span className="font-mono">{ctx.sitoLabel || ctx.numIscrSito}</span>
              {ctx.registroId ? (
                <>
                  {" · "}
                  Registro: <span className="font-mono">{ctx.registroId.slice(0, 12)}…</span>
                </>
              ) : null}
            </p>
          )}
          {syncRuns.data && syncRuns.data.length > 0 && (
            <p>Ultima sync: {syncRuns.data[0].resource} — {syncRuns.data[0].status}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" /> Certificato RENTRI (mTLS)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {certs.isLoading && <CenteredLoader />}
          {!certs.isLoading && !hasCert && (
            <p className="text-sm text-muted-foreground">
              Nessun certificato configurato. Carica un file P12 nel wizard interoperabilità sopra.
            </p>
          )}
          {hasCert && rentriHealth.isLoading && <CenteredLoader />}
          {hasCert && rentriHealth.data && (
            <Badge variant={rentriHealth.data.status === "ok" ? "success" : "destructive"}>
              {rentriHealth.data.status === "ok"
                ? "Connessione RENTRI OK"
                : String(rentriHealth.data.detail ?? "Errore connessione RENTRI")}
            </Badge>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Database className="h-4 w-4" /> Certificati caricati (archivio Fernet)
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {certs.isLoading && <CenteredLoader />}
          {certs.error && <ErrorState error={certs.error} />}
          {certs.data && certs.data.length === 0 && (
            <p className="px-6 py-6 text-sm text-muted-foreground">
              Nessun certificato in archivio. Usa il wizard sopra per caricare il tuo file P12.
            </p>
          )}
          {certs.data && certs.data.length > 0 && (
            <Table>
              <THead>
                <TR>
                  <TH>Etichetta</TH>
                  <TH>CN</TH>
                  <TH>CF / P.IVA</TH>
                  <TH>Validità</TH>
                  <TH />
                </TR>
              </THead>
              <TBody>
                {certs.data.map((c: CertificateItem) => (
                  <TR key={c.id}>
                    <TD className="font-medium">{c.label}</TD>
                    <TD className="font-mono text-xs">{c.subject_cn ?? "—"}</TD>
                    <TD className="font-mono text-xs">
                      {c.subject_cf ?? "—"}
                      {c.rentri_iscrizione ? (
                        <span className="block text-[10px] text-muted-foreground">
                          {c.rentri_iscrizione}
                        </span>
                      ) : null}
                    </TD>
                    <TD className="text-xs">
                      {c.expired ? (
                        <Badge variant="destructive">scaduto</Badge>
                      ) : (
                        <Badge variant="success">{formatDate(c.not_after)}</Badge>
                      )}
                    </TD>
                    <TD className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => remove.mutate(c.id)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
